import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import API_BASE from '../config';

const RATIOS = ['16:9', '9:16', '1:1', '4:3', '3:4', '3:2', '2:3'];

function useVideoPoller(token, requestId, onDone, onError) {
  const intervalRef = useRef(null);
  const elapsedRef = useRef(0);

  useEffect(() => {
    if (!requestId) return;
    elapsedRef.current = 0;
    intervalRef.current = setInterval(async () => {
      elapsedRef.current += 5;
      try {
        const res = await fetch(`${API_BASE}/api/poll/${requestId}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const data = await res.json();
        if (!data.success) { clearInterval(intervalRef.current); onError(data.error); return; }
        if (data.status === 'done')    { clearInterval(intervalRef.current); onDone(data.url); }
        if (data.status === 'failed')  { clearInterval(intervalRef.current); onError('영상 생성 실패'); }
      } catch (e) {
        clearInterval(intervalRef.current);
        onError(e.message);
      }
    }, 5000);
    return () => clearInterval(intervalRef.current);
  }, [requestId]);
}

export default function TextToVideo({ onSave }) {
  const { token } = useAuth();
  const [prompt, setPrompt] = useState('');
  const [resolution, setResolution] = useState('720p');
  const [duration, setDuration] = useState('10');
  const [ratio, setRatio] = useState('16:9');
  const [loading, setLoading] = useState(false);
  const [requestId, setRequestId] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [videoUrl, setVideoUrl] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!loading) return;
    const t = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(t);
  }, [loading]);

  useVideoPoller(
    token,
    requestId,
    (url) => { setVideoUrl(url); setLoading(false); onSave({ type: 't2v', prompt, url }); },
    (err) => { setError(err); setLoading(false); }
  );

  const generate = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    setError(null);
    setVideoUrl(null);
    setRequestId(null);
    setElapsed(0);
    try {
      const res = await fetch(`${API_BASE}/api/t2v`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ prompt, resolution, duration, ratio }),
      });
      const data = await res.json();
      if (data.success) { setRequestId(data.request_id); }
      else { setError(data.error); setLoading(false); }
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  };

  const cancel = () => { setRequestId(null); setLoading(false); setElapsed(0); };

  const download = () => {
    const a = document.createElement('a');
    a.href = videoUrl;
    a.download = `grok-t2v-${Date.now()}.mp4`;
    a.click();
  };

  const fmtTime = s => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div className="panel">
      <div className="panel-header">
        <div className="panel-icon purple">🎥</div>
        <div className="panel-title-group">
          <div className="panel-title">텍스트 → 영상</div>
          <div className="panel-sub">텍스트 프롬프트로 영상 생성</div>
        </div>
      </div>
      <div className="panel-body">
        <div className="form-col">
          <div className="form-group">
            <label>프롬프트</label>
            <textarea
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              placeholder="석양이 지는 해변, 파도가 부드럽게 밀려오는 장면..."
              rows={5}
            />
          </div>

          <div className="grid-2">
            <div className="form-group">
              <label>해상도</label>
              <select value={resolution} onChange={e => setResolution(e.target.value)}>
                <option value="480p">480p</option>
                <option value="720p">720p (최대)</option>
              </select>
            </div>
            <div className="form-group">
              <label>길이</label>
              <select value={duration} onChange={e => setDuration(e.target.value)}>
                <option value="5">5초</option>
                <option value="10">10초</option>
                <option value="15">15초 (최대)</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>화면 비율</label>
            <div className="ratio-grid">
              {RATIOS.map(r => (
                <button
                  key={r}
                  className="ratio-btn"
                  onClick={() => setRatio(r)}
                  style={{
                    border: ratio === r ? '2px solid var(--accent3)' : '1px solid var(--border)',
                    background: ratio === r ? 'rgba(102,68,255,0.12)' : 'var(--bg3)',
                    color: ratio === r ? 'var(--accent3)' : 'var(--text2)',
                    fontWeight: ratio === r ? '700' : '400',
                  }}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          {!loading ? (
            <button className="btn btn-purple" onClick={generate} disabled={!prompt.trim()}>
              ◉ 영상 생성
            </button>
          ) : (
            <button className="btn-cancel" onClick={cancel}>
              ✕ 취소
            </button>
          )}
          {error && <div className="error-box">⚠ {error}</div>}
        </div>

        <div className="output-area">
          <span className="output-label">결과</span>
          <div className="output-box">
            {loading ? (
              <div className="loading">
                <div className="loading-timer">{fmtTime(elapsed)}</div>
                <div className="loading-bar">
                  <div className="loading-bar-fill" style={{ background: 'var(--accent3)' }} />
                </div>
                <span className="loading-text">영상 생성 중... (보통 1~4분 소요)</span>
              </div>
            ) : videoUrl ? (
              <video src={videoUrl} controls autoPlay loop className="output-video" />
            ) : (
              <div className="output-placeholder">
                <span className="output-placeholder-icon">🎥</span>
                <span className="output-placeholder-text">영상이 여기 표시됩니다</span>
              </div>
            )}
          </div>
          {videoUrl && <button className="btn-download" onClick={download}>↓ MP4 다운로드</button>}
        </div>
      </div>
    </div>
  );
}
